<template>
  <div class="tw-overflow-x-scroll">
    <div v-if="label" class="tw-mb-1 tw-block tw-text-xs">{{ label }}</div>
    <div class="tw-flex tw-w-fit tw-min-w-full tw-gap-1 tw-rounded-md tw-border tw-p-1 tw-font-medium">
      <TooltipPopup
        v-for="button in buttons"
        :key="button.id"
        :position="tooltipPosition"
        tooltipClasses="!tw-w-56"
        class="tw-min-w-[80px] tw-flex-1 tw-cursor-pointer tw-rounded-sm tw-p-2 tw-text-center tw-text-xs hover:tw-bg-slate-100 dark:hover:tw-bg-slate-900"
        :class="{
          '!tw-bg-black !tw-text-white': isActive(button),
          '!tw-bg-slate-500 hover:!tw-bg-slate-500': isActive(button) && (isPickerDisabled || button.disabled),
          '!tw-cursor-not-allowed': isPickerDisabled || button.disabled
        }"
        @click="onClickPickerButton(button)"
      >
        <template #trigger>
          <button :disabled="isPickerDisabled || button.disabled" :data-testid="button.text">
            {{ button.text }}
            <span v-if="button?.label" :class="labelCss">{{ button.label }}</span>
          </button>
        </template>
        <template v-if="pickerDisabledReason" #content>
          {{ pickerDisabledReason }}
        </template>
        <template v-else-if="button.disabled" #content>
          <p>
            This feature is not enabled for your organization.
            <button
              class="tw-underline"
              @click="
                $emit('handleContactGoldcast', button.contactGoldcastText ? button.contactGoldcastText : button.text)
              "
            >
              Contact Goldcast
            </button>
            to get access.
          </p>
        </template>
      </TooltipPopup>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';

import TooltipPopup from '@/studioComponents/TooltipPopup.vue';

export interface Button {
  id: number | string;
  text: string;
  routeName?: string;
  label?: string;
  disabled?: boolean;
  contactGoldcastText?: string;
}

interface Props {
  text: string;
  position: 'bottom' | 'top' | 'left';
}

export default defineComponent({
  name: 'StudioSegmentedPicker',
  components: {
    TooltipPopup
  },
  props: {
    buttons: { type: Array as PropType<Button[]>, required: true },
    activeButtonId: {
      type: [Number, Array, String] as PropType<number | number[] | string | string[]>,
      required: true
    },
    label: {
      type: String,
      default: ''
    },
    isPickerDisabled: {
      type: Boolean,
      default: false
    },
    pickerDisabledReason: {
      type: String,
      default: ''
    },
    tooltipPosition: {
      type: String as PropType<Props['position']>,
      default: 'top',
      validator: (prop: Props['position']) => ['bottom', 'top', 'left'].includes(prop)
    },
    labelCss: {
      type: String,
      default: 'tw-text-deep-orange-400'
    }
  },
  emits: {
    handleContactGoldcast: (text: string) => {
      return typeof text === 'string';
    },
    'picker-click': (button: Button) => {
      return typeof button === 'object';
    }
  },
  methods: {
    isActive(button: Button): boolean {
      return Array.isArray(this.activeButtonId as Array<number | string>)
        ? (this.activeButtonId as Array<number | string>).includes(button.id)
        : this.activeButtonId === button.id;
    },
    onClickPickerButton(button: Button) {
      if (this.isPickerDisabled || button.disabled) {
        return;
      }
      this.$emit('picker-click', button);
    }
  }
});
</script>
